import { filter, map } from 'lodash';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userState } from 'state';

import {
  EnumProcessorSchedulingStatus,
  FilterFindManyProcessorSchedulingInput,
  ProcessorScheduling,
  SortFindManyProcessorSchedulingInput,
  useProcessorSchedulingPaginationQuery,
} from 'lib/graphql';
import { isBuyerOnly } from 'lib/userHelpers';

import {
  getProcessorSchedulingQueryFilters,
  shownStatuses,
} from 'pages/processor/dashboard/filters';

import { PaletteKey } from '../shared/jobs-by-state';

const PER_PAGE = 15;

export type JobsByStateData = {
  title: string;
  paletteKey: PaletteKey;
  count: number;
  loading: boolean;
  jobs: ProcessorScheduling[];
  hideAttention?: boolean;
  defaultExpanded?: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
};

function usePaginatedSchedulingQuery(
  queryFilter: FilterFindManyProcessorSchedulingInput,
  skip: boolean,
  sort = SortFindManyProcessorSchedulingInput.DropoffdateDesc,
) {
  const [page, setPage] = useState(1);
  const { data, loading } = useProcessorSchedulingPaginationQuery({
    fetchPolicy: 'network-only',
    skip,
    variables: {
      sort,
      page,
      perPage: PER_PAGE,
      filter: queryFilter,
    },
  });

  const pagination = data?.processorSchedulingPagination;

  return {
    jobs: (pagination?.items as ProcessorScheduling[]) ?? [],
    count: pagination?.count ?? 0,
    loading,
    hasNextPage: pagination?.pageInfo?.hasNextPage ?? false,
    hasPreviousPage: pagination?.pageInfo?.hasPreviousPage ?? page > 1,
    page: pagination?.pageInfo?.currentPage ?? page,
    perPage: PER_PAGE,
    onPageChange: setPage,
  };
}

export function useProducerJobs() {
  const user = useRecoilValue(userState);
  const buyerMode = user ? isBuyerOnly(user) : false;

  const {
    pendingApprovals: pendingApprovalsFilter,
    bookingsInProgress: bookingsInProgressFilter,
    bookingsCompleted: completedBookingsFilter,
  } = getProcessorSchedulingQueryFilters({
    customerId: user?._id,
    buyerOnly: buyerMode,
    producerOnly: !buyerMode,
  });

  const scheduledFilter = {
    ...pendingApprovalsFilter,
    status: EnumProcessorSchedulingStatus.Scheduled,
  };

  const inProgressWithoutScheduledFilter = {
    ...bookingsInProgressFilter,
    AND: [
      {
        OR: map(
          filter(
            shownStatuses,
            (s) =>
              s !== EnumProcessorSchedulingStatus.Scheduled &&
              s !== EnumProcessorSchedulingStatus.Completed,
          ),
          (s) => ({ status: s }),
        ),
      },
    ],
  };

  const pendingResult = usePaginatedSchedulingQuery(
    pendingApprovalsFilter,
    !user || buyerMode,
  );
  const scheduledResult = usePaginatedSchedulingQuery(
    scheduledFilter,
    !user,
    SortFindManyProcessorSchedulingInput.DropoffdateAsc,
  );
  const inProgressResult = usePaginatedSchedulingQuery(
    inProgressWithoutScheduledFilter,
    !user,
  );
  const completedResult = usePaginatedSchedulingQuery(
    completedBookingsFilter,
    !user,
  );

  const columns: JobsByStateData[] = [
    // "Pending Approval" is a producer concern; skip for buyers
    ...(!buyerMode
      ? [
          {
            title: 'Pending Approval',
            paletteKey: 'primary' as PaletteKey,
            ...pendingResult,
          },
        ]
      : []),
    {
      title: 'Scheduled',
      paletteKey: 'success' as PaletteKey,
      ...scheduledResult,
    },
    {
      title: 'In Progress',
      paletteKey: 'success' as PaletteKey,
      ...inProgressResult,
    },
    {
      title: 'Inactive',
      paletteKey: 'secondary' as PaletteKey,
      ...completedResult,
      hideAttention: true,
      defaultExpanded: false,
    },
  ];

  return { user, columns, buyerMode };
}
